import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginRequest, RegisterRequest } from '../types';
import { authApi, userApi } from '../services/api';
import { extractErrorResult } from '../utils/errorMessages';
import { websocketService } from '../services/websocket';
import { useOrganizationStore } from './organizationStore';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  validationErrors: Array<{ field: string; message: string }>;
  registrationSuccessEmail: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  updateProfile: (data: Partial<User>) => Promise<boolean>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  clearError: () => void;
  clearRegistrationSuccess: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      validationErrors: [],
      registrationSuccessEmail: null,

      clearError: () => {
        set({ error: null, validationErrors: [] });
      },

      clearRegistrationSuccess: () => {
        set({ registrationSuccessEmail: null });
      },

      login: async (credentials) => {
        set({ isLoading: true, error: null, validationErrors: [] });
        try {
          const response = await authApi.login(credentials.email, credentials.password, credentials.captchaToken);
          if (response.success && response.data) {
            const { accessToken, refreshToken, user } = response.data as { accessToken: string; refreshToken: string; user: User };
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            // Connect WebSocket after successful login
            websocketService.connect(accessToken);
            // Set org context for WebSocket room membership (REV-7.2)
            if (user.activeOrganizationId) {
              websocketService.switchOrg(user.activeOrganizationId);
            }
            set({ user, isAuthenticated: true, isLoading: false });
            // Sync server's activeOrganizationId to org store for persistence across sessions
            if (user.activeOrganizationId) {
              useOrganizationStore.setState({ activeOrganizationId: user.activeOrganizationId });
            } else {
              useOrganizationStore.setState({ activeOrganizationId: null, organizations: [] });
            }
          } else {
            set({ 
              error: response.error?.message || 'Invalid email or password. Please check your credentials and try again.', 
              isLoading: false 
            });
          }
        } catch (error: unknown) {
          const { message, validationErrors } = extractErrorResult(error);
          set({ 
            error: message, 
            validationErrors, 
            isLoading: false 
          });
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null, validationErrors: [] });
        try {
          const response = await authApi.register(data);
          if (response.success && response.data) {
            // SECURITY: Never auto-login after registration.
            // Regardless of whether the backend returns tokens, the user must
            // verify their email and log in manually.
            set({
              registrationSuccessEmail: data.email,
              isLoading: false,
            });
          } else {
            set({ error: response.error?.message || 'Registration failed', isLoading: false });
          }
        } catch (error: unknown) {
          const { message, validationErrors } = extractErrorResult(error);
          set({ 
            error: message, 
            validationErrors, 
            isLoading: false 
          });
        }
      },

      logout: async () => {
        // Revoke the token on the server BEFORE clearing local state.
        // Without this call, the old access token remains valid on the
        // server for its entire 15-minute TTL after the user "logs out".
        // Disconnect WebSocket before clearing state
        websocketService.reset();
        try {
          await authApi.logout();
        } catch {
          // Ignore network errors — still clear local state so the user
          // isn't stuck in a logged-in UI with a broken connection.
        }
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Clear organization store so next login starts fresh (prevents stale org from previous user)
        useOrganizationStore.setState({ activeOrganizationId: null, organizations: [] });
        set({ user: null, isAuthenticated: false, error: null });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await authApi.me();
          if (response.success && response.data) {
            set({ user: response.data as User, isAuthenticated: true, isLoading: false });
            // REV-01 fix: Connect WebSocket after successful token validation on page refresh.
            // Without this, checkAuth() validates the JWT but never opens the WS connection,
            // so real-time events are missing after a page reload.
            const currentToken = localStorage.getItem('accessToken');
            if (currentToken) {
              websocketService.connect(currentToken);
            }
            // Set org context for WebSocket room membership (REV-7.2)
            const activeOrgId = (response.data as User).activeOrganizationId;
            if (activeOrgId) {
              websocketService.switchOrg(activeOrgId);
            }
            // Sync server's activeOrganizationId to org store for persistence across sessions
            if (activeOrgId) {
              useOrganizationStore.setState({ activeOrganizationId: activeOrgId });
            } else {
              useOrganizationStore.setState({ activeOrganizationId: null, organizations: [] });
            }
          } else {
            set({ isAuthenticated: false, user: null, isLoading: false });
          }
        } catch {
          set({ isAuthenticated: false, user: null, isLoading: false });
        }
      },

      updateUser: (data) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...data } });
        }
      },

      updateProfile: async (data) => {
        try {
          const response = await userApi.updateProfile(data as unknown as Record<string, unknown>);
          if (response.success && response.data) {
            set({ user: response.data as User });
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },

      updatePassword: async (currentPassword, newPassword) => {
        try {
          const response = await userApi.updatePassword(currentPassword, newPassword);
          return response.success;
        } catch {
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ isAuthenticated: state.isAuthenticated }),
    }
  )
);

// REV-F1: Register WebSocket auth error handler for automatic token refresh + reconnect.
// When WS detects token expiry (e.g. after 15min JWT expires), this handler
// attempts a silent token refresh and reconnects WS — no user action needed.
const WS_API_URL = import.meta.env.VITE_API_URL || '/api';
websocketService.onAuthError(async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    // No refresh token — force login
    websocketService.reset();
    localStorage.removeItem('accessToken');
    window.location.href = '/app/login';
    return;
  }
  try {
    // Use raw axios (not api instance) to avoid interceptor infinite loop
    const { data } = await (await import('axios')).default.post(`${WS_API_URL}/auth/refresh`, { refreshToken });
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);
    websocketService.connect(data.data.accessToken);
  } catch {
    // Refresh failed — force login
    if (import.meta.env?.DEV) {
      console.warn('[Auth] Token refresh failed after WS auth error, redirecting to login');
    }
    websocketService.reset();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/app/login';
  }
});
